import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, ExternalLink, Pencil, Plus, RefreshCw, Search, Star, Trash2, Undo2, Send, Video } from 'lucide-react';
import { articlesService, ArticleAdminStats, ArticleCategoryAdmin } from '../services/api';
import { Article, ArticleStatus } from '../types';
import { BrandSpinnerRing } from '../components/SiteBrandedLoader';
import BlogSectionNav from './blog/BlogSectionNav';
import ConfirmDialog from './blog/ConfirmDialog';
import { apiErrorMessage, articleStatus, faDateTime, faNumber, imagePreviewUrl, publicArticleUrl, STATUS_META } from './blog/blogAdmin';

type StatusFilter = 'all' | 'draft' | 'scheduled' | 'published';
type SortKey = 'newest' | 'oldest' | 'updated' | 'popular' | 'title';

const PAGE_SIZE = 12;

const STATUS_TABS: Array<{ key: StatusFilter | 'featured'; label: string; statKey: keyof ArticleAdminStats }> = [
  { key: 'all', label: 'همه', statKey: 'all' },
  { key: 'published', label: 'منتشر شده', statKey: 'published' },
  { key: 'draft', label: 'پیش‌نویس', statKey: 'draft' },
  { key: 'scheduled', label: 'زمان‌بندی‌شده', statKey: 'scheduled' },
  { key: 'featured', label: 'منتخب', statKey: 'featured' },
];

const StatusBadge: React.FC<{ status: ArticleStatus; publishedAt?: string | null }> = ({ status, publishedAt }) => (
  <span className={`inline-flex flex-col items-start gap-0.5 rounded-lg px-2.5 py-1 text-[11px] font-black ring-1 ${STATUS_META[status].className}`}>
    {STATUS_META[status].label}
    {status === 'SCHEDULED' && publishedAt && <span className="font-medium">{faDateTime(publishedAt)}</span>}
  </span>
);

/** فهرست مقالات وبلاگ: جست‌وجو، فیلتر وضعیت/دسته، مرتب‌سازی، اکشن‌های سریع و گروهی */
const Articles: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = (searchParams.get('status') as StatusFilter | 'featured' | null) || 'all';
  const categoryId = searchParams.get('category') || '';
  const tag = searchParams.get('tag') || '';
  const sort = (searchParams.get('sort') as SortKey | null) || 'newest';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const searchQuery = searchParams.get('q') || '';

  const [searchDraft, setSearchDraft] = useState(searchQuery);
  const [articles, setArticles] = useState<Article[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState<ArticleAdminStats | null>(null);
  const [categories, setCategories] = useState<ArticleCategoryAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [confirm, setConfirm] = useState<{ type: 'delete' | 'bulk-delete'; article?: Article } | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const setParam = useCallback(
    (patch: Record<string, string | null>, resetPage = true) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      if (resetPage) next.delete('page');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  // جست‌وجوی با تأخیر
  useEffect(() => {
    if (searchDraft === searchQuery) return undefined;
    const t = window.setTimeout(() => setParam({ q: searchDraft.trim() || null }), 400);
    return () => window.clearTimeout(t);
  }, [searchDraft, searchQuery, setParam]);

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 3000);
  }, []);

  const loadStats = useCallback(() => {
    articlesService.getStats().then(setStats).catch(() => setStats(null));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await articlesService.getAll({
        page,
        limit: PAGE_SIZE,
        status: status === 'featured' ? 'all' : status,
        featured: status === 'featured' ? true : undefined,
        categoryId: categoryId || undefined,
        tag: tag || undefined,
        search: searchQuery || undefined,
        sort,
      });
      setArticles(Array.isArray(response?.data) ? response.data : []);
      setTotal(response?.meta?.total ?? 0);
      setTotalPages(Math.max(1, response?.meta?.totalPages ?? 1));
      setSelected([]);
    } catch (err) {
      setError(apiErrorMessage(err, 'خطا در دریافت مقالات'));
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [page, status, categoryId, tag, searchQuery, sort]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadStats();
    articlesService.getManageCategories().then(setCategories).catch(() => setCategories([]));
  }, [loadStats]);

  const refresh = () => {
    load();
    loadStats();
  };

  const runAction = async (article: Article, action: () => Promise<unknown>, message: string) => {
    setBusyId(article.id);
    try {
      await action();
      showToast(message);
      refresh();
    } catch (err) {
      showToast(apiErrorMessage(err, 'عملیات انجام نشد'));
    } finally {
      setBusyId(null);
    }
  };

  const togglePublish = (a: Article) => {
    const st = articleStatus(a);
    if (st === 'DRAFT') runAction(a, () => articlesService.publish(a.id), 'مقاله منتشر شد');
    else runAction(a, () => articlesService.unpublish(a.id), 'مقاله به پیش‌نویس برگشت');
  };

  const toggleFeatured = (a: Article) =>
    runAction(a, () => articlesService.update(a.id, { isFeatured: !a.isFeatured }), a.isFeatured ? 'از منتخب‌ها حذف شد' : 'به «منتخب من حقیقی» اضافه شد');

  const doConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    try {
      if (confirm.type === 'delete' && confirm.article) {
        await articlesService.delete(confirm.article.id);
        showToast('مقاله حذف شد');
      } else if (confirm.type === 'bulk-delete') {
        await articlesService.bulkDelete(selected);
        showToast(`${faNumber(selected.length)} مقاله حذف شد`);
      }
      setConfirm(null);
      refresh();
    } catch (err) {
      showToast(apiErrorMessage(err, 'حذف انجام نشد'));
    } finally {
      setConfirmBusy(false);
    }
  };

  const bulkUpdate = async (data: { published?: boolean; isFeatured?: boolean }, message: string) => {
    if (!selected.length) return;
    setLoading(true);
    try {
      await articlesService.bulkUpdate(selected, data as any);
      showToast(message);
      refresh();
    } catch (err) {
      showToast(apiErrorMessage(err, 'عملیات گروهی انجام نشد'));
      setLoading(false);
    }
  };

  const allSelected = articles.length > 0 && selected.length === articles.length;
  const categoryName = useMemo(() => categories.find((c) => c.id === categoryId)?.name, [categories, categoryId]);

  return (
    <div className="space-y-5">
      <BlogSectionNav
        title="مدیریت وبلاگ"
        description="نوشتن، زمان‌بندی و انتشار مقاله‌های «مجله من حقیقی»؛ هر تغییر بلافاصله در سایت اعمال می‌شود."
        action={
          <div className="flex gap-2">
            <button type="button" onClick={refresh} className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50">
              <RefreshCw className="h-4 w-4" aria-hidden />
              بروزرسانی
            </button>
            <Link to="/articles/new" className="inline-flex items-center gap-2 rounded-xl bg-[#4a2e68] px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-[#392352]">
              <Plus className="h-4 w-4" aria-hidden />
              مقالهٔ جدید
            </Link>
          </div>
        }
      />

      {/* تب وضعیت */}
      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((t) => {
          const active = status === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setParam({ status: t.key === 'all' ? null : t.key })}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-bold transition-colors ${
                active ? 'border-[#4a2e68] bg-[#4a2e68] text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-violet-200 hover:bg-violet-50'
              }`}
            >
              {t.label}
              <span className={`rounded-full px-2 text-xs ${active ? 'bg-white/20' : 'bg-gray-100 text-gray-600'}`}>{stats ? faNumber(stats[t.statKey]) : '…'}</span>
            </button>
          );
        })}
      </div>

      {/* فیلترها */}
      <div className="grid grid-cols-1 gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm md:grid-cols-4">
        <label className="relative md:col-span-2">
          <span className="sr-only">جست‌وجو</span>
          <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
          <input
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder="جست‌وجو در عنوان، خلاصه، متن یا برچسب…"
            className="w-full rounded-xl border border-gray-200 py-2 pl-3 pr-9 text-sm focus:border-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-100"
          />
        </label>
        <select value={categoryId} onChange={(e) => setParam({ category: e.target.value || null })} className="rounded-xl border border-gray-200 px-3 py-2 text-sm">
          <option value="">همهٔ دسته‌ها</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({faNumber(c.articleCount)})
            </option>
          ))}
        </select>
        <select value={sort} onChange={(e) => setParam({ sort: e.target.value === 'newest' ? null : e.target.value })} className="rounded-xl border border-gray-200 px-3 py-2 text-sm">
          <option value="newest">جدیدترین ایجاد</option>
          <option value="updated">آخرین ویرایش</option>
          <option value="oldest">قدیمی‌ترین</option>
          <option value="popular">پربازدیدترین</option>
          <option value="title">عنوان (الفبا)</option>
        </select>
        {(tag || categoryName || searchQuery) && (
          <div className="flex flex-wrap items-center gap-2 text-xs md:col-span-4">
            <span className="text-gray-500">فیلتر فعال:</span>
            {searchQuery && <span className="rounded-full bg-gray-100 px-2.5 py-1 font-bold text-gray-700">«{searchQuery}»</span>}
            {categoryName && <span className="rounded-full bg-violet-50 px-2.5 py-1 font-bold text-violet-800">دسته: {categoryName}</span>}
            {tag && <span className="rounded-full bg-violet-50 px-2.5 py-1 font-bold text-violet-800">برچسب: {tag}</span>}
            <button
              type="button"
              onClick={() => {
                setSearchDraft('');
                setSearchParams(new URLSearchParams(status !== 'all' ? { status } : {}));
              }}
              className="font-bold text-[#4a2e68] hover:underline"
            >
              پاک‌کردن
            </button>
          </div>
        )}
      </div>

      {/* اکشن گروهی */}
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm">
          <span className="font-bold text-violet-900">{faNumber(selected.length)} مقاله انتخاب شده</span>
          <button type="button" onClick={() => bulkUpdate({ published: true }, 'مقالات انتخابی منتشر شدند')} className="rounded-lg bg-white px-3 py-1.5 font-bold text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-50">
            انتشار
          </button>
          <button type="button" onClick={() => bulkUpdate({ published: false }, 'مقالات انتخابی به پیش‌نویس برگشتند')} className="rounded-lg bg-white px-3 py-1.5 font-bold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
            بازگشت به پیش‌نویس
          </button>
          <button type="button" onClick={() => bulkUpdate({ isFeatured: true }, 'به منتخب‌ها اضافه شدند')} className="rounded-lg bg-white px-3 py-1.5 font-bold text-amber-700 ring-1 ring-amber-200 hover:bg-amber-50">
            افزودن به منتخب
          </button>
          <button type="button" onClick={() => setConfirm({ type: 'bulk-delete' })} className="rounded-lg bg-white px-3 py-1.5 font-bold text-red-700 ring-1 ring-red-200 hover:bg-red-50">
            حذف
          </button>
          <button type="button" onClick={() => setSelected([])} className="mr-auto text-xs font-bold text-violet-800 hover:underline">
            لغو انتخاب
          </button>
        </div>
      )}

      {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>}

      {/* جدول */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {loading && articles.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <BrandSpinnerRing size="md" />
          </div>
        ) : articles.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm font-bold text-gray-800">مقاله‌ای با این فیلترها پیدا نشد</p>
            <Link to="/articles/new" className="rounded-xl bg-[#4a2e68] px-4 py-2 text-sm font-bold text-white hover:bg-[#392352]">
              نوشتن اولین مقاله
            </Link>
          </div>
        ) : (
          <div className={`overflow-x-auto ${loading ? 'opacity-60' : ''}`}>
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50 text-xs font-bold text-gray-500">
                <tr>
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      aria-label="انتخاب همه"
                      checked={allSelected}
                      onChange={(e) => setSelected(e.target.checked ? articles.map((a) => a.id) : [])}
                      className="h-4 w-4 accent-[#4a2e68]"
                    />
                  </th>
                  <th className="px-4 py-3 text-right">مقاله</th>
                  <th className="px-4 py-3 text-right">دسته</th>
                  <th className="px-4 py-3 text-right">وضعیت</th>
                  <th className="px-4 py-3 text-right">بازدید</th>
                  <th className="px-4 py-3 text-right">آخرین ویرایش</th>
                  <th className="px-4 py-3 text-right">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {articles.map((a) => {
                  const st = articleStatus(a);
                  const thumb = a.featuredImage || imagePreviewUrl(a.featuredImageKey);
                  const busy = busyId === a.id;
                  return (
                    <tr key={a.id} className={`align-top hover:bg-gray-50/70 ${busy ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          aria-label={`انتخاب ${a.title}`}
                          checked={selected.includes(a.id)}
                          onChange={(e) => setSelected((prev) => (e.target.checked ? [...prev, a.id] : prev.filter((id) => id !== a.id)))}
                          className="h-4 w-4 accent-[#4a2e68]"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex min-w-[280px] items-start gap-3">
                          <div className="h-14 w-24 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                            {thumb && <img src={thumb} alt="" className="h-full w-full object-cover" loading="lazy" />}
                          </div>
                          <div className="min-w-0 space-y-1">
                            <button type="button" onClick={() => navigate(`/articles/${a.id}/edit`)} className="text-right font-bold leading-6 text-gray-900 hover:text-[#4a2e68]">
                              {a.title}
                            </button>
                            <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                              <span dir="ltr" className="max-w-[220px] truncate">
                                /{a.slug}
                              </span>
                              {a.isFeatured && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-bold text-amber-700">
                                  <Star className="h-3 w-3 fill-amber-400 text-amber-500" aria-hidden />
                                  منتخب
                                </span>
                              )}
                              {a.hasVideo && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 font-bold text-violet-700">
                                  <Video className="h-3 w-3" aria-hidden />
                                  ویدیو
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-xs font-bold text-gray-700">{a.category?.name || <span className="text-gray-400">—</span>}</td>
                      <td className="px-4 py-4">
                        <StatusBadge status={st} publishedAt={a.publishedAt} />
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-700">{faNumber(a.viewCount)}</td>
                      <td className="whitespace-nowrap px-4 py-4 text-xs text-gray-600">{faDateTime(a.updatedAt)}</td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap items-center gap-1">
                          <Link to={`/articles/${a.id}/edit`} title="ویرایش" className="rounded-lg p-2 text-gray-600 hover:bg-violet-50 hover:text-[#4a2e68]">
                            <Pencil className="h-4 w-4" aria-hidden />
                            <span className="sr-only">ویرایش</span>
                          </Link>
                          <a
                            href={publicArticleUrl(a, st !== 'PUBLISHED')}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={st === 'PUBLISHED' ? 'مشاهده در سایت' : 'پیش‌نمایش'}
                            className="rounded-lg p-2 text-gray-600 hover:bg-violet-50 hover:text-[#4a2e68]"
                          >
                            {st === 'PUBLISHED' ? <ExternalLink className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
                            <span className="sr-only">{st === 'PUBLISHED' ? 'مشاهده در سایت' : 'پیش‌نمایش'}</span>
                          </a>
                          <button
                            type="button"
                            onClick={() => togglePublish(a)}
                            disabled={busy}
                            title={st === 'DRAFT' ? 'انتشار' : 'بازگشت به پیش‌نویس'}
                            className={`rounded-lg p-2 ${st === 'DRAFT' ? 'text-emerald-700 hover:bg-emerald-50' : 'text-slate-600 hover:bg-slate-100'}`}
                          >
                            {st === 'DRAFT' ? <Send className="h-4 w-4" aria-hidden /> : <Undo2 className="h-4 w-4" aria-hidden />}
                            <span className="sr-only">{st === 'DRAFT' ? 'انتشار' : 'بازگشت به پیش‌نویس'}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleFeatured(a)}
                            disabled={busy}
                            title={a.isFeatured ? 'حذف از منتخب' : 'افزودن به منتخب'}
                            className="rounded-lg p-2 text-amber-600 hover:bg-amber-50"
                          >
                            <Star className={`h-4 w-4 ${a.isFeatured ? 'fill-amber-400' : ''}`} aria-hidden />
                            <span className="sr-only">{a.isFeatured ? 'حذف از منتخب' : 'افزودن به منتخب'}</span>
                          </button>
                          <button type="button" onClick={() => setConfirm({ type: 'delete', article: a })} title="حذف" className="rounded-lg p-2 text-red-600 hover:bg-red-50">
                            <Trash2 className="h-4 w-4" aria-hidden />
                            <span className="sr-only">حذف</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* صفحه‌بندی */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm shadow-sm">
          <span className="text-gray-600">
            صفحهٔ {faNumber(page)} از {faNumber(totalPages)} — {faNumber(total)} مقاله
          </span>
          <div className="flex gap-2">
            <button type="button" disabled={page <= 1} onClick={() => setParam({ page: String(page - 1) }, false)} className="rounded-lg border border-gray-200 px-3 py-1.5 font-bold text-gray-700 disabled:opacity-40">
              قبلی
            </button>
            <button type="button" disabled={page >= totalPages} onClick={() => setParam({ page: String(page + 1) }, false)} className="rounded-lg border border-gray-200 px-3 py-1.5 font-bold text-gray-700 disabled:opacity-40">
              بعدی
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirm}
        danger
        busy={confirmBusy}
        title={confirm?.type === 'bulk-delete' ? 'حذف مقالات انتخابی' : 'حذف مقاله'}
        confirmLabel="حذف قطعی"
        message={
          confirm?.type === 'bulk-delete' ? (
            <>
              {faNumber(selected.length)} مقاله برای همیشه حذف می‌شوند. این کار برگشت‌پذیر نیست؛ اگر فقط می‌خواهید از سایت برداشته شوند، «بازگشت به پیش‌نویس» را بزنید.
            </>
          ) : (
            <>
              مقالهٔ «{confirm?.article?.title}» برای همیشه حذف می‌شود. این کار برگشت‌پذیر نیست؛ برای برداشتن موقت از سایت، آن را به پیش‌نویس برگردانید.
            </>
          )
        }
        onConfirm={doConfirm}
        onClose={() => setConfirm(null)}
      />

      {toast && (
        <div role="status" className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-bold text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
};

export default Articles;
